// Copyright 2026 Hardfin, Inc.

/**
 * React hook for fetching lot details for a specific item at a specific location.
 * This module provides functionality for requesting lot-level inventory breakdown
 * data from the remote API using React Query.
 */
import {
  GetInventoryLotsOptions,
  getInventoryLots,
} from '@/inventory/api/get-item-lots';
import { InventoryLotsResponse } from '@/inventory/api/types';
import { UseQueryResult, useQuery } from '@tanstack/react-query';

/**
 * Configuration options for the useItemLots hook.
 */
interface UseItemLotsOptions {
  itemKey: string;
  locationKey: string;
  includeEmptyLots?: boolean;
  enabled?: boolean;
}

/**
 * Return type for the useItemLots hook.
 */
interface UseItemLotsResult {
  /**
   * The lots response data containing the list of lots for the item at location
   */
  lotsData?: InventoryLotsResponse;

  /**
   * Whether the initial data is being loaded
   */
  isLoading: boolean;

  /**
   * Whether a background fetch is in progress
   */
  isFetching: boolean;

  /**
   * Whether the query has encountered an error
   */
  isError: boolean;

  /**
   * The error object if the query failed
   */
  error: Error | null;

  /**
   * Function to manually trigger a refetch
   */
  refetch: UseQueryResult<InventoryLotsResponse>['refetch'];
}

/**
 * A React hook that fetches lot details for a specific item at a specific location.
 * This hook fetches the lot-level breakdown showing batch info and quantity details.
 *
 * The hook supports:
 * - Required item key and location key
 * - Optional flag to include zero-quantity (empty) lots
 * - Automatic background updates and caching via React Query
 * - Manual refetch capability
 * - Loading and error state management
 *
 * @param options - Configuration options including item key, location key, and empty lots flag
 * @returns Object containing the lots data and query state
 *
 * @example
 * ```tsx
 * // Fetch lots for an item at a location (only non-empty lots)
 * const { lotsData, isLoading } = useItemLots({
 *   itemKey: 'item_abc123',
 *   locationKey: 'loc_xyz789',
 * });
 *
 * // Fetch including empty (zero-quantity) lots
 * const { lotsData, isLoading } = useItemLots({
 *   itemKey: 'item_abc123',
 *   locationKey: 'loc_xyz789',
 *   includeEmptyLots: true,
 * });
 * ```
 */
export function useItemLots(options: UseItemLotsOptions): UseItemLotsResult {
  const {
    itemKey,
    locationKey,
    includeEmptyLots = false,
    enabled = true,
  } = options;

  const queryOptions: GetInventoryLotsOptions = {
    itemKey,
    locationKey,
    includeEmptyLots,
  };

  const queryResult = useQuery({
    queryKey: ['inventory', 'lots', itemKey, locationKey, includeEmptyLots],
    queryFn: () => getInventoryLots(queryOptions),
    enabled: enabled && Boolean(itemKey) && Boolean(locationKey),
    staleTime: 0,
    refetchOnWindowFocus: true,
  });

  return {
    lotsData: queryResult.data,
    isLoading: queryResult.isLoading,
    isFetching: queryResult.isFetching,
    isError: queryResult.isError,
    error: queryResult.error,
    refetch: queryResult.refetch,
  };
}
